import {
  AnyaAgentDetails,
  AnyaAgentSummary,
} from "views/Automations/AnyaAgents/types";

import { SortInfo } from "./pagination";

export enum AnyaInvocationSource {
  WIDGET = "WIDGET",
}

/**
 * Request information used to generate unique API key for widget data caching
 */
export type ApiRequestInfo = {
  url: string;
  method?: "GET" | "POST" | "PUT" | "DELETE";
  data?: any;
  params?: any;
};

export type PreFetchedApiData = {
  [apiKey: string]: unknown;
};

export type StoreWidgetDataPayload = {
  widgetId: string;
  apiKey: string;
  data: unknown;
};

export type AnyaInvocationContext = {
  source: AnyaInvocationSource;
  context: Record<string, unknown>;
};

type AnyaAgentId = AnyaAgentSummary["id"];

type AnyaConversationAgentBase = Pick<
  AnyaAgentSummary,
  "id" | "name" | "description" | "agent_type" | "createdBy"
>;

export type AnyaConversationAgentMeta = AnyaConversationAgentBase &
  Pick<AnyaAgentDetails, "prompt" | "context"> & {
    starterPrompts?: string[];
  };

type AnyaSessionAgentFields = {
  sessionType?: AnyaSessionType;
  agentId?: AnyaAgentId;
  agentName?: AnyaConversationAgentMeta["name"];
  agentDescription?: AnyaConversationAgentMeta["description"];
  starterPrompts?: AnyaConversationAgentMeta["starterPrompts"];
  agent?: AnyaConversationAgentMeta;
};

type AnyaAssistantContextFields = {
  widgetTitle: string;
  widgetType: string;
  dashboardId: string;
  conversationId: string;
  locale: string;
  zoneId: string;
  invocationContext: AnyaInvocationContext;
  agentId: AnyaAgentId;
};

export type AnyaRequestDto = {
  query: string;
  // conversation_history  to be added
  conversation_history: Array<object>;
} & Partial<AnyaAssistantContextFields>;

export enum AnyaSessionMode {
  CHAT = "chat",
  AGENT = "agent",
}

export type AnyaSessionType = `${AnyaSessionMode}`;

export type AnyaConversationUserMessage = {
  text?: string;
  type?: string;
};

export type AnyaConversationMessageResponse = {
  reasoningSummary?: string;
  details?: string;
  visualisation?: Array<VisualisationObject | WidgetVisualisationObject> | null;
  displayStatus?: string | null;
  nextQuestions?: string[] | null;
  requestId?: string | null;
  conversationId?: string | null;
  status?: string | null;
  eventData?: object | null;
};

export type AnyaConversationMessage = {
  userMessage?: AnyaConversationUserMessage;
  response?: AnyaConversationMessageResponse | null;
  messageId?: string | null;
  isThumbsUp?: boolean | null;
  status?: string | null;
  displayStatus?: string | null;
};

export type AnyaConversationHistoryItem = AnyaSessionAgentFields & {
  conversationId: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  widgetId: string;
};

export type AnyaConversationMessagesResponse = AnyaSessionAgentFields & {
  conversations?: AnyaConversationMessage[];
  personaId?: number | null;
  personaName?: string | null;
};

export type AnyaFeedbackDto = {
  isThumbsUp?: boolean;
  messageId: string;
  feedback?: string;
};
export type AnyaConversationRequestDto = {
  conversationId: string;
  page: number;
  size: number;
};
export type AnyaEventDto = {
  event: {
    isConfirmed: boolean;
  };
  requestId: string;
  conversationId: string;
};
export type AnyaRetryDto = {
  query: string;
  requestId: string;
} & Required<
  Pick<
    AnyaAssistantContextFields,
    | "widgetTitle"
    | "widgetType"
    | "dashboardId"
    | "conversationId"
    | "locale"
    | "zoneId"
  >
> &
  Partial<Pick<AnyaAssistantContextFields, "invocationContext" | "agentId">>;
export type AnyaCancelDto = {
  query: string;
  requestId: string;
  conversationId: string;
};
export type AnyaResponseDto = {
  reasoningSummary: string;
  details: string;
  visualisation: Array<VisualisationObject | WidgetVisualisationObject>;
  displayStatus?: string | null;
  nextQuestions?: AnyaConversationMessageResponse["nextQuestions"];
  requestId: string;
  conversationId: string;
  status: string;
  eventData: object;
};

/**
 * Legacy visualization object (chart, trend, table, bar, code)
 */
type VisualisationObject = {
  type: AnyaVisualationType;
  title: string;
  data: Array<object>;
  columns: Array<object>;
};

/**
 * New widget-based visualization object
 * When widgetId is present, UI renders an actual dashboard widget
 */
export type WidgetVisualisationObject = {
  widgetId: string;
  widgetType: string;
  displayType: string;
  config: WidgetConfig;
  filters?: WidgetFilters;
  title?: string;
  description?: string;
  /** Pre-fetched API data keyed by API key (SHA-256 hash) */
  data?: PreFetchedApiData;
  /** Sort configuration from Anya BE (e.g., { severity: "descend" }) */
  sortInfo?: SortInfo;
};

export type WidgetConfig = {
  column1?: string;
  column2?: string;
  tagKey1?: string;
  tagKey2?: string;
  showLegends?: boolean;
  legendPosition?: string;
  legendDisplayValue?: string;
  orderBy?: string;
  showTotalGroups?: boolean;
  showRiskScore?: boolean;
  showDataLabels?: boolean;
  dataSource?: string;
};
export type WidgetFilters = {
  status?: string[];
  severity?: string[];
  product?: number[];
  subProduct?: number[];
  team?: number[];
  armorcodeProjects?: number[];
  createdAt?: { from?: string; to?: string };
  [key: string]: unknown;
};

export function isWidgetVisualisation(
  v: VisualisationObject | WidgetVisualisationObject,
): v is WidgetVisualisationObject {
  return "widgetId" in v && "widgetType" in v;
}

export enum AnyaVisualationType {
  BAR = "BAR",
  PIE = "PIE",
  LINE = "LINE",
  TABLE = "TABLE",
  CODE = "CODE",
}
